import { Api } from "../net/api";
import { audio } from "../fx/audio";

/**
 * SOUND AND MUSIC, ADJUSTABLE MID-MATCH, SAVED TO THE PROFILE.
 *
 * ⭐ Alex, 2026-08-14: *"we need a settings button on the game page so players can adjust the sound
 * and adjust music. This settings should save per the player's profile."*
 *
 * ⚠️ **PER PROFILE, NOT PER BROWSER, AND THAT IS THE WHOLE REQUIREMENT.** The volumes were living in
 * `localStorage`: a different setting on every device, wiped by clearing site data. A player who
 * muted the game on a laptop was blasted by it on their phone. `Revival/Settings` on our server
 * owns them now, keyed by the same profile everything else is keyed by.
 *
 * ⭐ **IT APPLIES LIVE AND SAVES LATE.** Dragging a slider changes the volume on the same frame -
 * a volume control you cannot hear while adjusting is unusable - but the write is debounced, so a
 * drag is one request rather than sixty.
 *
 * ⚠️ **A FAILED SAVE MUST NOT REVERT THE SLIDER.** The player's intent stands locally even if the
 * server is unreachable; the panel says the save failed rather than silently snapping back, which
 * would read as the control being broken.
 *
 * ⛔ **NOT A ROUTE.** It is an overlay: opening settings must never leave a match. Navigating away
 * from a live board to change the volume would abandon the turn.
 */

const SAVE_DEBOUNCE_MS = 500;

let panel: HTMLElement | null = null;
let saveTimer: number | null = null;
let loaded = false;

/**
 * Pull the player's saved volumes and apply them. Safe to call on every screen mount.
 *
 * 🚨 **IT RETURNS WHETHER IT WORKED, AND THAT IS NOT DECORATION.** The first version swallowed the
 * failure and returned `void`, so a request made before the auth token was ready left the volumes
 * on their defaults permanently and NOTHING said so - the player's saved setting simply did not
 * apply, and every caller believed it had. A silent failure in a loader is indistinguishable from a
 * player who never changed the setting.
 *
 * ⚠️ **ONE RETRY, BECAUSE THE FAILURE IS ALMOST ALWAYS A RACE.** Boot order can put this ahead of
 * the token; a single short retry covers that without turning a genuinely offline server into a
 * retry storm.
 */
export async function loadSettings(): Promise<boolean> {
  if (loaded) return true;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const s = await Api.settings() as { sfxVolume?: number; musicVolume?: number };
      const sfx = clamp(s?.sfxVolume, audio.volumes.sfx);
      const music = clamp(s?.musicVolume, audio.volumes.music);
      audio.setVolumes(sfx, music);
      loaded = true;
      return true;
    } catch (e) {
      if (attempt === 0) {
        await new Promise((r) => window.setTimeout(r, 400));
        continue;
      }
      // ⚠️ Non-fatal by design: unreachable settings must not stop a match starting. But it is
      // LOGGED, so "my volume did not stick" is diagnosable instead of mysterious.
      console.warn("[settings] could not load your saved volumes:",
                   e instanceof Error ? e.message : String(e));
    }
  }
  return false;
}

/**
 * The volumes as THIS module's audio controller sees them.
 *
 * ⚠️ **EXISTS SO A CHECK CANNOT ACCIDENTALLY MEASURE A SECOND MODULE INSTANCE.** A harness that
 * imports `fx/audio.ts` itself can, under Vite's HMR, receive a different instance from the one the
 * app is using (the app holds `...?t=<timestamp>` after a hot update). It then reads defaults while
 * the real controller has the right values, and reports a product bug that does not exist - the same
 * duplicate-module trap that once produced two `.stage` elements. Reading through the owner removes
 * the ambiguity entirely.
 */
export function currentVolumes(): { sfx: number; music: number } {
  return audio.volumes;
}

/**
 * What the music is doing, as THIS module's controller sees it.
 *
 * ⚠️ Same reason as `currentVolumes`: a harness that imports `fx/audio.ts` itself can get a second
 * instance under HMR and read `music: null` from a controller nobody is using. This module is
 * imported by the screens, so it holds the one the app holds.
 */
export function currentMusic(): ReturnType<typeof audio.musicInfo> {
  return audio.musicInfo();
}

/** Whether audio has been allowed to start. */
export function isAudioArmed(): boolean {
  return audio.isArmed;
}

function clamp(v: unknown, fallback: number): number {
  const n = typeof v === "number" ? v : Number.NaN;
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.min(1, n));
}

/** The gear. Returns the button so a screen can place it in its own furniture. */
export function settingsButton(): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = "utilbtn settings__open";
  b.dataset["label"] = "SETTINGS";
  b.setAttribute("aria-label", "Sound settings");
  b.textContent = "SOUND";
  b.addEventListener("click", () => toggleSettings());
  return b;
}

export function toggleSettings(): void {
  if (panel) {
    closeSettings();
    return;
  }
  openSettings();
}

export function closeSettings(): void {
  panel?.remove();
  panel = null;
  window.removeEventListener("keydown", onKey);
}

function onKey(e: KeyboardEvent): void {
  // ⚠️ Escape closes it. A modal over a live board that can only be dismissed by finding its button
  // again is a trap, and the turn clock is still running behind it.
  if (e.key === "Escape") closeSettings();
}

function openSettings(): void {
  void loadSettings();

  const root = document.createElement("div");
  root.className = "settings";
  root.setAttribute("role", "dialog");
  root.setAttribute("aria-label", "Sound settings");

  const card = document.createElement("div");
  card.className = "settings__card";

  const h = document.createElement("h2");
  h.className = "settings__title";
  h.textContent = "SOUND";

  const status = document.createElement("p");
  status.className = "settings__status";
  status.textContent = "Saved to your profile.";

  const sfx = slider("SOUND EFFECTS", audio.volumes.sfx, (v) => {
    audio.setVolumes(v, audio.volumes.music);
    // ⭐ A tick on release so the player HEARS the level they just chose. A volume slider with no
    // audible reference is guesswork.
    audio.play("click", 1);
    queueSave(status);
  });

  const music = slider("MUSIC", audio.volumes.music, (v) => {
    audio.setVolumes(audio.volumes.sfx, v);
    queueSave(status);
  });

  const close = document.createElement("button");
  close.type = "button";
  close.className = "plate plate--quiet settings__close";
  close.dataset["label"] = "CLOSE";
  const face = document.createElement("span");
  face.className = "plate__face";
  face.setAttribute("aria-hidden", "true");
  const label = document.createElement("span");
  label.className = "plate__label";
  label.textContent = "CLOSE";
  close.append(face, label);
  close.addEventListener("click", () => closeSettings());

  card.append(h, sfx, music, status, close);
  root.append(card);

  // Clicking the scrim closes it, the same as Escape.
  root.addEventListener("click", (e) => {
    if (e.target === root) closeSettings();
  });

  document.body.append(root);
  panel = root;
  window.addEventListener("keydown", onKey);
}

function slider(label: string, value: number, onInput: (v: number) => void): HTMLElement {
  const wrap = document.createElement("label");
  wrap.className = "settings__row";

  const name = document.createElement("span");
  name.className = "settings__label";
  name.textContent = label;

  const readout = document.createElement("span");
  readout.className = "settings__value";
  readout.textContent = pct(value);

  const input = document.createElement("input");
  input.type = "range";
  input.min = "0";
  input.max = "100";
  input.step = "1";
  input.value = String(Math.round(value * 100));
  input.className = "settings__range";
  input.addEventListener("input", () => {
    const v = Number(input.value) / 100;
    readout.textContent = pct(v);
    onInput(v);
  });

  wrap.append(name, input, readout);
  return wrap;
}

function pct(v: number): string {
  return `${Math.round(v * 100)}%`;
}

function queueSave(status: HTMLElement): void {
  if (saveTimer !== null) window.clearTimeout(saveTimer);
  saveTimer = window.setTimeout(async () => {
    saveTimer = null;
    const { sfx, music } = audio.volumes;
    try {
      await Api.saveSettings(sfx, music);
      status.textContent = "Saved to your profile.";
      status.classList.remove("settings__status--error");
    } catch (e) {
      // ⛔ The slider is NOT reverted - see the class note.
      status.textContent = `Could not save to your profile: ${
        e instanceof Error ? e.message : String(e)}`;
      status.classList.add("settings__status--error");
    }
  }, SAVE_DEBOUNCE_MS);
}
